import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, LoadingButton } from "@/components/common";
import { ROLE_LABELS } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus } from "lucide-react";

const ROLES = Object.keys(ROLE_LABELS);

function UserDialog({ user, open, onOpenChange, onSaved, permissionConfig }) {
  const { branches, user: actor } = useAuth();
  const blank = { name: "", email: "", password: "", role: "staff", branch_ids: [], active: true, login_methods: ["password"], permissions: [] };
  const [form, setForm] = useState(blank);
  const [loading, setLoading] = useState(false);
  const [inviteUrl, setInviteUrl] = useState("");
  useEffect(() => { if (open) { setForm(user ? { ...user, password: "" } : { ...blank, permissions: permissionConfig?.role_defaults?.staff || [] }); setInviteUrl(""); } }, [open, user, permissionConfig]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleBranch = (id) => set("branch_ids", form.branch_ids?.includes(id) ? form.branch_ids.filter((x) => x !== id) : [...(form.branch_ids || []), id]);
  const toggleMethod = (method) => set("login_methods", form.login_methods?.includes(method) ? form.login_methods.filter((x) => x !== method) : [...(form.login_methods || []), method]);
  const togglePermission = (permission) => {
    let selected = form.permissions?.includes(permission)
      ? form.permissions.filter((value) => value !== permission)
      : [...(form.permissions || []), permission];
    if (permission === "page.attendance" && !selected.includes(permission)) selected = selected.filter((value) => !value.startsWith("attendance."));
    if (permission.startsWith("attendance.") && !selected.includes("page.attendance")) selected.push("page.attendance");
    set("permissions", selected);
  };
  const changeRole = (role) => setForm((current) => ({ ...current, role, permissions: permissionConfig?.role_defaults?.[role] || [] }));

  const save = async () => {
    if (!form.name || !form.email) return toast.error("Name and email required");
    if (!form.login_methods?.length) return toast.error("Select at least one login method");
    if (form.password && form.password.length < 12) return toast.error("Password must be at least 12 characters");
    if (["branch_manager", "staff"].includes(form.role) && !form.branch_ids?.length) return toast.error("Assign at least one branch");
    setLoading(true);
    try {
      let data;
      if (user) ({ data } = await api.put(`/auth/users/${user.id}`, { name: form.name, email: form.email, role: form.role, branch_ids: form.branch_ids, active: form.active, login_methods: form.login_methods, permissions: form.permissions, ...(form.password ? { password: form.password } : {}) }));
      else ({ data } = await api.post("/auth/users", { name: form.name, email: form.email, password: form.password || null, role: form.role, branch_ids: form.branch_ids, login_methods: form.login_methods, permissions: form.permissions }));
      toast.success("User saved. Their open session will refresh permissions automatically."); onSaved();
      if (data.invite_url) setInviteUrl(data.invite_url); else onOpenChange(false);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  const scoped = !["super_admin", "management", "director"].includes(form.role);
  const createInvite = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/auth/users/${user.id}/invite`);
      setInviteUrl(data.invite_url);
      toast.success("New 48-hour invitation created");
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  const copyInvite = async () => {
    await navigator.clipboard.writeText(inviteUrl);
    toast.success("Invitation link copied");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{user ? "Edit User" : "Add User"}</DialogTitle></DialogHeader>
        {inviteUrl ? <div className="space-y-4">
          <div className="rounded-sm border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">User is ready. Send this link by WhatsApp or email. Password setup links expire in 48 hours.</div>
          <Input readOnly value={inviteUrl} />
          <Button className="w-full bg-blue-600 hover:bg-blue-700" onClick={copyInvite}>Copy invitation link</Button>
        </div> : <div className="space-y-3">
          <div><Label className="text-xs">Name</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="user-name" /></div>
          <div><Label className="text-xs">Email / Gmail</Label><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="user-email" />
            {user && form.login_methods?.includes("google") && form.email !== user.email && <p className="text-[11px] text-amber-700 mt-1">The old Google identity will be disconnected. This new email will bind on its first verified Google login.</p>}
          </div>
          {user && <div><Label className="text-xs">Account status</Label>
            <Select value={form.active === false ? "deactivated" : "active"} onValueChange={(value) => set("active", value === "active")}>
              <SelectTrigger data-testid="user-status"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="deactivated" disabled={user.id === actor?.id}>Deactivated</SelectItem></SelectContent>
            </Select>
            {form.active === false && <p className="text-[11px] text-red-600 mt-1">This user will be blocked from all login and API access immediately after saving.</p>}
          </div>}
          <div>
            <Label className="text-xs">Login methods</Label>
            <div className="mt-1 space-y-2 border border-slate-200 rounded-sm p-3">
              <label className="flex items-center gap-2 text-sm"><Checkbox checked={form.login_methods?.includes("google")} onCheckedChange={() => toggleMethod("google")} /> Google email (Gmail or Workspace)</label>
              <label className="flex items-center gap-2 text-sm"><Checkbox checked={form.login_methods?.includes("password")} onCheckedChange={() => toggleMethod("password")} /> Email and password</label>
            </div>
          </div>
          {form.login_methods?.includes("password") && <div><Label className="text-xs">{user ? "New Password (blank = keep)" : "Password (optional — blank sends setup link)"}</Label><Input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="user-password" /><p className="text-[11px] text-slate-500 mt-1">If entered, use at least 12 characters.</p></div>}
          <div><Label className="text-xs">Role</Label>
            <Select value={form.role} onValueChange={changeRole}>
              <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
              <SelectContent>{ROLES.filter((r) => actor?.role === "super_admin" || r !== "super_admin").map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Page & feature permissions</Label>
            <p className="text-[11px] text-slate-500 mt-1">Changing the role loads a suggested template. You can then tick or untick each item.</p>
            <div className="mt-2 border border-slate-200 rounded-sm p-3 space-y-4">
              {Object.entries(permissionConfig?.groups || {}).map(([group, permissions]) => <div key={group}>
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">{group}</div>
                <div className="grid sm:grid-cols-2 gap-2">
                  {Object.entries(permissions).map(([key, label]) => <label key={key} className="flex items-start gap-2 text-sm cursor-pointer">
                    <Checkbox checked={form.permissions?.includes(key)} onCheckedChange={() => togglePermission(key)} data-testid={`permission-${key}`} />
                    <span>{label}</span>
                  </label>)}
                </div>
              </div>)}
            </div>
          </div>
          {scoped && (
            <div><Label className="text-xs">Assigned Branches (scopes their access)</Label>
              <div className="grid grid-cols-2 gap-1 mt-1 max-h-40 overflow-y-auto border border-slate-200 rounded-sm p-2">
                {branches.map((b) => (
                  <label key={b.id} className="flex items-center gap-1.5 text-sm cursor-pointer">
                    <Checkbox checked={form.branch_ids?.includes(b.id)} onCheckedChange={() => toggleBranch(b.id)} data-testid={`user-branch-${b.code}`} /> {b.name}
                  </label>
                ))}
              </div>
            </div>
          )}
          {user && <Button type="button" variant="outline" className="w-full" onClick={createInvite} disabled={loading}>Create new invitation link</Button>}
        </div>}
        <DialogFooter>{inviteUrl ? <Button onClick={() => onOpenChange(false)}>Done</Button> : <LoadingButton loading={loading} onClick={save} className="bg-blue-600 hover:bg-blue-700" data-testid="user-save-btn">Save</LoadingButton>}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function UsersPage() {
  const { hasRole } = useAuth();
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [permissionConfig, setPermissionConfig] = useState(null);

  const load = useCallback(async () => { try { const [{ data: rows }, { data: config }] = await Promise.all([api.get("/auth/users"), api.get("/auth/permissions")]); setUsers(rows); setPermissionConfig(config); } catch (e) { toast.error(apiError(e)); } }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <PageHeader title="Users & Access" subtitle="Assign a role template, branch scope, and exact page or attendance permissions for each person."
        testid="users-header"
        actions={hasRole("super_admin", "management") && <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => { setEditing(null); setOpen(true); }} data-testid="add-user-btn"><Plus className="h-4 w-4 mr-1" /> Add User</Button>} />
      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
            <th className="py-3 px-4">Name</th><th className="py-3 px-4">Email</th><th className="py-3 px-4">Role</th><th className="py-3 px-4">Login</th><th className="py-3 px-4">Branches</th><th className="py-3 px-4">Status</th><th className="py-3 px-4"></th>
          </tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`user-row-${u.email}`}>
                <td className="py-3 px-4 font-medium">{u.name}</td>
                <td className="py-3 px-4 text-slate-500">{u.email}</td>
                <td className="py-3 px-4"><Badge variant="secondary">{ROLE_LABELS[u.role]}</Badge></td>
                <td className="py-3 px-4 text-slate-500 text-xs">{u.login_methods?.map((m) => m === "google" ? "Google" : "Password").join(" + ") || "—"}</td>
                <td className="py-3 px-4 text-slate-500 text-xs">{u.branch_ids?.length ? `${u.branch_ids.length} branch(es)` : "All"}</td>
                <td className="py-3 px-4">{u.active === false ? <Badge className="bg-slate-200 text-slate-600">Inactive</Badge> : <Badge className="bg-emerald-100 text-emerald-800">Active</Badge>}</td>
                <td className="py-3 px-4 text-right">{hasRole("super_admin", "management") && <Button size="sm" variant="ghost" className="h-7 text-xs text-blue-600" onClick={() => { setEditing(u); setOpen(true); }} data-testid={`edit-user-${u.email}`}>Edit</Button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <UserDialog user={editing} open={open} onOpenChange={setOpen} onSaved={load} permissionConfig={permissionConfig} />
    </div>
  );
}
